import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";
import EmailIcon from "@mui/icons-material/Email";
import AddIcon from "@mui/icons-material/Add";
import { useTranslation } from "react-i18next";
import { Toolbar } from "../../components/ui/toolbar/toolbar.component";
import { Action } from "../../types/toolbar";
import CustomModal from "../../components/ui/modal/modal.component";
import { useState, useEffect } from "react";
import { SETTLEMENTS_ENDPOINT, ERROR_SNACKBAR, SUCCESS_SNACKBAR } from "../../consts";
import DynamicForm from "../../components/ui/dynamicForm/dynamicForm.component";
import EquipmentCounterForm from "../../components/ui/equipmentCounterForm/equipmentCounterForm.component";
import { useParams, useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import { useCRUD } from "../../api/crud";
import { Box, Typography, Alert, Button } from "@mui/material";
import { useHasPermission } from "../../contexts/auth";
import { DynamicFieldsConfig } from "../../types/dynamicForm";
import { Settlement } from "../../types/settlements";
import { getSettlementPrimaryContractTerm, getSettlementTermPrimaryEquipmentId } from "../../utils/settlementContractTerms";
import { SendReminder } from "../../components/settlements/sendReminder/sendReminder.component";
import axios from "../../api/axios";

const SETTLEMENT_STATUS_CONFIRMED = "C";
const CAN_CHANGE_CONFIRMED_SETTLEMENT_PERMISSION =
  "settlement.can_change_confirmed_settlement";

export const SettlementsDetailsActions = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { loading, error, remove, get } = useCRUD(SETTLEMENTS_ENDPOINT);
  const [updateSettlementOpened, setUpdateSettlementOpened] = useState<boolean>(false);
  const [deleteSettlementOpened, setDeleteSettlementOpened] = useState<boolean>(false);
  const [sendReminderOpened, setSendReminderOpened] = useState<boolean>(false);
  const [addCounterOpened, setAddCounterOpened] = useState<boolean>(false);
  const [canSendReminder, setCanSendReminder] = useState<boolean>(false);
  const [equipmentId, setEquipmentId] = useState<string | null>(null);
  const [settlementStatus, setSettlementStatus] = useState<string | null>(null);
  const [confirmedChangeModalOpen, setConfirmedChangeModalOpen] = useState(false);
  const canChangeSettlement = useHasPermission("settlement.change_settlement");
  const canChangeConfirmedSettlement = useHasPermission(
    CAN_CHANGE_CONFIRMED_SETTLEMENT_PERMISSION
  );
  const canDeleteSettlement = useHasPermission("settlement.delete_settlement");
  const canAddCounter = useHasPermission("settlement.add_equipmentcounter");

  const isSettlementConfirmed = settlementStatus === SETTLEMENT_STATUS_CONFIRMED;
  const canRefreshSettlement = canChangeSettlement && !isSettlementConfirmed;
  const canModifySettlement =
    canChangeSettlement && (!isSettlementConfirmed || canChangeConfirmedSettlement);
  const requiresConfirmedChangeConfirmation =
    isSettlementConfirmed && canChangeConfirmedSettlement;

  useEffect(() => {
    const fetchSettlement = async () => {
      if (id) {
        const result = await get(id);
        if (result?.data) {
          const settlementData = result.data as Settlement;

          // Check if settlement meets criteria for sending reminder
          const hasNeedsInvestigation = settlementData.status === "N"; // NEEDS_INVESTIGATION
          const hasOutdatedCounter = settlementData.positions?.some(
            (position) => position.investigation_reasons?.includes("O") // OUTDATED_COUNTER
          ) || false;

          setCanSendReminder(hasNeedsInvestigation && hasOutdatedCounter);
          setEquipmentId(
            (() => {
              const primaryTerm = getSettlementPrimaryContractTerm(settlementData);
              return primaryTerm
                ? getSettlementTermPrimaryEquipmentId(primaryTerm) ?? null
                : null;
            })()
          );
          setSettlementStatus(settlementData.status ?? null);
        }
      }
    };
    fetchSettlement();
    window.addEventListener("settlementUpdated", fetchSettlement);

    return () => {
      window.removeEventListener("settlementUpdated", fetchSettlement);
    };
  }, [id, get]);

  const handleDeleteSettlement = async () => {
    if (loading || !id) {
      return;
    }

    const result = await remove({ id });

    if (result?.success) {
      enqueueSnackbar(t("Settlement deleted successfully"), SUCCESS_SNACKBAR);
      setDeleteSettlementOpened(false);
      navigate("/settlements");
    } else {
      enqueueSnackbar(t("Failed to delete settlement"), ERROR_SNACKBAR);
    }
  };

  const handleRefreshSettlement = async () => {
    if (loading || !id) {
      return;
    }

    try {
      const result = await axios.post(
        `${SETTLEMENTS_ENDPOINT}${id}/refresh-calculation/`
      );
      if (!result?.data) {
        throw new Error("No data returned");
      }

      enqueueSnackbar(t("Settlement refreshed successfully"), SUCCESS_SNACKBAR);
      window.dispatchEvent(new Event("settlementUpdated"));
      // Refetch settlement to update canSendReminder
      const updatedResult = await get(id);
      if (updatedResult?.data) {
        const settlementData = updatedResult.data as Settlement;
        const hasNeedsInvestigation = settlementData.status === "N";
        const hasOutdatedCounter = settlementData.positions?.some(
          (position) => position.investigation_reasons?.includes("O")
        ) || false;
        setCanSendReminder(hasNeedsInvestigation && hasOutdatedCounter);
      }
    } catch (e) {
      const backendDetail =
        (e as any)?.response?.data?.detail ||
        (e as any)?.response?.data?.message ||
        (e as any)?.detail ||
        "";

      enqueueSnackbar(
        backendDetail || t("Failed to refresh settlement"),
        ERROR_SNACKBAR
      );
    }
  };

  const requestUpdateSettlement = () => {
    if (requiresConfirmedChangeConfirmation) {
      setConfirmedChangeModalOpen(true);
      return;
    }

    setUpdateSettlementOpened(true);
  };

  const handleConfirmConfirmedSettlementChange = () => {
    setConfirmedChangeModalOpen(false);
    setUpdateSettlementOpened(true);
  };

  const confirmedChangeModal = canChangeConfirmedSettlement ? (
    <CustomModal
      opened={confirmedChangeModalOpen}
      setOpened={setConfirmedChangeModalOpen}
      title={t("Confirm change to confirmed settlement")}
      width={480}
      content={
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography variant="body1">
            {t(
              "Are you sure you want to edit this confirmed settlement? Saving changes will recalculate totals and may override values."
            )}
          </Typography>
          <Alert severity="warning">
            <Typography variant="body2">
              {t("This settlement is already confirmed.")}
            </Typography>
          </Alert>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, flexWrap: "wrap" }}>
            <Button
              variant="outlined"
              onClick={() => setConfirmedChangeModalOpen(false)}
            >
              {t("Cancel")}
            </Button>
            <Button variant="contained" color="warning" onClick={handleConfirmConfirmedSettlementChange}>
              {t("Confirm")}
            </Button>
          </Box>
        </Box>
      }
    />
  ) : null;

  const actions: Action[] = [
    {
      icon: <RefreshIcon />,
      label: t("Refresh settlement"),
      onClick: () => void handleRefreshSettlement(),
      disabled: !canRefreshSettlement || loading,
    },
    {
      icon: <EmailIcon />,
      label: t("Send reminder"),
      onClick: () => {
        setSendReminderOpened(true);
      },
      disabled: !canModifySettlement || !canSendReminder || loading,
    },
    {
      icon: <EditIcon />,
      label: t("Update settlement"),
      onClick: requestUpdateSettlement,
      disabled: !canModifySettlement || loading,
    },
    {
      icon: <AddIcon />,
      label: t("Add new counter"),
      onClick: () => {
        setAddCounterOpened(true);
      },
      disabled: !canAddCounter || !equipmentId,
    },
    {
      icon: <DeleteIcon />,
      label: t("Delete settlement"),
      onClick: () => {
        setDeleteSettlementOpened(true);
      },
      color: "var(--errorColor)",
      disabled: !canDeleteSettlement,
    },
  ];

  const dynamicFields: DynamicFieldsConfig = {
    contract_term_id: [
      { dependsOn: "contract_term_id", value: id, action: undefined },
    ],
    inflation_rate_percent: [
      {
        dependsOn: "show_inflation_rate_percent",
        value: true,
        action: "show",
      },
    ],
  };

  const errorMessage =
    typeof error === "string"
      ? error
      : error?.message ?? error?.detail ?? "";

  const deleteModalContent = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography variant="body2" color="text.secondary">
        {t(
          "Deleting this settlement will permanently remove it from the system. This action cannot be undone."
        )}
      </Typography>

      <Alert severity="warning">
        <Typography variant="body2">
          {t(
            "This action cannot be undone. All associated data will be permanently deleted."
          )}
        </Typography>
      </Alert>

      {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

      <Button
        variant="contained"
        color="error"
        onClick={handleDeleteSettlement}
        disabled={loading}
      >
        {loading ? t("Processing...") : t("Confirm deletion")}
      </Button>
    </Box>
  );

  return (
    <>
      <Toolbar actions={actions} />
      <CustomModal
        opened={updateSettlementOpened}
        setOpened={setUpdateSettlementOpened}
        title={t("Update settlement")}
        content={
          <DynamicForm
            url={SETTLEMENTS_ENDPOINT}
            id={id}
            onSuccess={() => {
              setUpdateSettlementOpened(false);
              window.dispatchEvent(new Event("settlementUpdated"));
            }}
            dynamicFields={dynamicFields}
          />
        }
      />
      <CustomModal
        opened={deleteSettlementOpened}
        setOpened={setDeleteSettlementOpened}
        title={t("Delete settlement")}
        content={deleteModalContent}
      />
      <CustomModal
        opened={sendReminderOpened}
        setOpened={setSendReminderOpened}
        title={t("Send reminder")}
        content={
          <SendReminder
            onSuccess={() => {
              setSendReminderOpened(false);
              window.dispatchEvent(new Event("settlementUpdated"));
            }}
            preselectedSettlementId={id}
          />
        }
      />
      <CustomModal
        opened={addCounterOpened}
        setOpened={setAddCounterOpened}
        title={t("Add new counter")}
        content={
          <EquipmentCounterForm
            onSuccess={() => {
              setAddCounterOpened(false);
              window.dispatchEvent(new Event("equipmentCounterAdded"));
            }}
            equipmentId={equipmentId}
            hiddenFields={["equipment_id", "job"]}
          />
        }
      />
      {confirmedChangeModal}
    </>
  );
};
